import * as React from 'react';
import { createPortal } from 'react-dom';

import { Button } from './button';
import { cn } from './lib/cn';
import { getFocusableElements } from './lib/focus';

/*
 * Modal primitive — phase-c batch-6 spec.
 *
 * - `size`: sm 440px (confirmations), md 560px (forms), lg 720px, xl 820px,
 *   `full` viewport-relative (see `sizeClass`).
 * - Form modals use radius-2xl + shadow-xl; smaller variants use radius-lg + shadow-lg.
 * - Click-on-backdrop closes confirmations but NOT form modals (data-loss protection)
 *   — opt-in via `dismissOnBackdrop`.
 * - Esc closes by default; opt out with `dismissOnEscape={false}`.
 * - `confirmOnClose` (default true) guards every dismiss path (Esc, the X
 *   button, backdrop) behind a "discard changes?" step so a stray keypress
 *   can't wipe an in-progress form. Set `confirmOnClose={false}` on modals
 *   with nothing to lose (confirmation prompts, read-only info dialogs).
 *   Localize the prompt copy app-wide via `ModalDefaultsProvider`, or
 *   per-modal via the `confirm*` props.
 * - `dirty` (default true) lets a form skip the discard prompt when nothing
 *   has actually changed: the confirmation only appears when
 *   `confirmOnClose && dirty`, otherwise the dialog dismisses straight away.
 *   Leaving it unset preserves the historical confirm-always behavior.
 * - Focus: opening moves focus into the dialog, Tab/Shift+Tab cycle within it,
 *   and closing returns focus to whatever had it before (opt out with
 *   `restoreFocus={false}` when the previously-focused element is being
 *   unmounted anyway, e.g. a dialog that closes because it navigated away).
 */

type Size = 'sm' | 'md' | 'lg' | 'xl' | 'full';
type Placement = 'center' | 'right';

/*
 * Centered card: bounded max-width, rounded corners + drop shadow.
 *
 * `full` is the one size that is NOT a width budget: it is viewport-relative,
 * for content whose width is not ours to choose. The bounded sizes assume the
 * dialog's content reflows to whatever card it is given; an externally-authored
 * document (a Wertgutachten behind `sandbox=""`) does not, so no fixed card can
 * be guaranteed to fit it — `xl` clears A4 (~794px) by 26px BEFORE the body's
 * default `px-6`, and a real report carrying print margins is ~945px wide and
 * overflows `xl` outright (IMM-609).
 *
 * The two calcs mirror the backdrop's own padding — `p-6` (1.5rem a side, 3rem
 * total) and `py-12` (3rem a side, 6rem total) — so the card fills the backdrop
 * exactly and never makes it scroll. Pair it with `bodyClassName` to drop the
 * default `px-6 py-6`, or the padding eats the width the size just bought.
 */
/*
 * The centred card's height budget, in one place.
 *
 * The backdrop pads `py-12` — 3rem above and 3rem below — so `100vh - 6rem` is
 * exactly what a card may occupy without making the backdrop scroll. Every size
 * spells it from these two constants rather than restating the literal, so the
 * budget and the padding can never drift apart (they are literal strings, not
 * interpolations, because Tailwind's scanner only sees classes written out in
 * full).
 *
 * `max-h` for the bounded sizes and `h` for `full`: a short dialog must keep its
 * natural height, while `full` is deliberately viewport-filling. Either way the
 * card is bounded, which is what makes the body's `flex-1 overflow-y-auto`
 * actually engage — unbounded, the body is never smaller than its content, never
 * scrolls, and the overflow escapes to the backdrop, which scrolls the header and
 * footer off-screen with it (IMM-637 / BL-054).
 */
/*
 * Below `sm` a centred dialog is a BOTTOM SHEET, at every size.
 *
 * Not because a 440px card overflows a 390px screen — it does not, `max-w`
 * shrinks it — but because of where the hands are. A centred card puts its
 * actions in the middle of a screen held at the bottom, and its dismiss
 * affordance (the backdrop) in the corners a thumb reaches worst. A sheet is
 * anchored where the thumb already is.
 *
 * `dvh`, not `vh`: on iOS Safari `100vh` is the viewport WITHOUT the collapsing
 * toolbars, so a `vh`-bounded sheet is taller than the screen for as long as
 * they are showing — which is exactly while the user is reaching for it.
 *
 * The size is not ignored below `sm`, it is simply not a width budget there:
 * every sheet is full-bleed, and `size` resumes meaning something at `sm`.
 */
const SHEET = 'max-h-[85dvh] w-full max-w-none rounded-b-none rounded-t-2xl shadow-xl';
const SM_CENTERED_MAX_HEIGHT = 'sm:max-h-[calc(100vh-6rem)]';
const SM_CENTERED_FULL_HEIGHT = 'sm:h-[calc(100vh-6rem)]';
// A stacked card: the backdrop's 3rem top padding plus 4rem, and the same 4rem off the height budget.
const SM_STACKED_TOP = 'sm:pt-28';
const SM_STACKED_MAX_HEIGHT = 'sm:max-h-[calc(100vh-10rem)]';
const SM_STACKED_FULL_HEIGHT = 'sm:h-[calc(100vh-10rem)]';

const sizeClass: Record<Size, string> = {
  sm: `${SHEET} ${SM_CENTERED_MAX_HEIGHT} sm:max-w-[440px] sm:rounded-lg sm:shadow-lg`,
  md: `${SHEET} ${SM_CENTERED_MAX_HEIGHT} sm:max-w-[560px] sm:rounded-2xl sm:shadow-xl`,
  lg: `${SHEET} ${SM_CENTERED_MAX_HEIGHT} sm:max-w-[720px] sm:rounded-2xl sm:shadow-xl`,
  xl: `${SHEET} ${SM_CENTERED_MAX_HEIGHT} sm:max-w-[820px] sm:rounded-2xl sm:shadow-xl`,
  full: `${SHEET} ${SM_CENTERED_FULL_HEIGHT} sm:max-w-[calc(100vw-3rem)] sm:rounded-2xl sm:shadow-xl`,
};

// Right side-panel (drawer): same width budget but full-height, square, anchored
// to the right edge with a left hairline + shadow ("side-panel language"). The
// drawer has no backdrop padding to subtract, so `full` is simply unbounded.
const panelSizeClass: Record<Size, string> = {
  sm: 'max-w-[440px]',
  md: 'max-w-[560px]',
  lg: 'max-w-[720px]',
  xl: 'max-w-[820px]',
  full: 'max-w-none',
};

type ConfirmCopy = {
  readonly title: string;
  readonly description: string;
  readonly confirmLabel: string;
  readonly cancelLabel: string;
};

const FALLBACK_CONFIRM_COPY: ConfirmCopy = {
  title: 'Discard changes?',
  description: 'If you close now, your unsaved changes will be lost.',
  confirmLabel: 'Discard',
  cancelLabel: 'Keep editing',
};

type ModalDefaults = {
  readonly confirmClose: ConfirmCopy;
  readonly closeLabel: string;
};

const ModalDefaultsContext = React.createContext<ModalDefaults>({
  confirmClose: FALLBACK_CONFIRM_COPY,
  closeLabel: 'Close',
});

const ModalRequestCloseContext = React.createContext<(() => void) | null>(null);

/**
 * The enclosing `Modal`'s close request — the same path as Escape and the header
 * close button, so it raises the discard prompt when the form is dirty. A footer
 * "Cancel" calls this rather than the raw `onClose`, which would skip the prompt.
 */
export function useModalRequestClose(): () => void {
  const requestClose = React.useContext(ModalRequestCloseContext);

  if (requestClose === null) throw new Error('useModalRequestClose must be used inside a Modal');

  return requestClose;
}

/**
 * Supplies localized default copy for the close-confirmation step and the
 * header close button's accessible name to every `Modal` beneath it. Wrap
 * the app root once (below the i18n provider) so individual modals don't
 * each have to thread the strings through.
 */
export function ModalDefaultsProvider({
  confirmClose,
  closeLabel,
  children,
}: {
  readonly confirmClose: ConfirmCopy;
  readonly closeLabel: string;
  readonly children: React.ReactNode;
}) {
  const { title, description, confirmLabel, cancelLabel } = confirmClose;
  const value = React.useMemo<ModalDefaults>(
    () => ({ confirmClose: { title, description, confirmLabel, cancelLabel }, closeLabel }),
    [title, description, confirmLabel, cancelLabel, closeLabel],
  );
  return <ModalDefaultsContext.Provider value={value}>{children}</ModalDefaultsContext.Provider>;
}

type ModalProps = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly size?: Size;
  /**
   * Where the dialog sits. `center` (default) is the classic centered card;
   * `right` renders a full-height side panel anchored to the right edge
   * (drawer). Existing callers are unaffected — the default is `center`.
   */
  readonly placement?: Placement;
  /**
   * Opened over another dialog that stays open. From `sm` up the card sits 4rem lower, so the dialog behind keeps
   * its header (title and close button) in view, and the height cap shrinks by the same 4rem so the backdrop never
   * scrolls. Centred placement only: a bottom sheet (below `sm`) and `placement="right"` ignore it (IMM-1344).
   */
  readonly stacked?: boolean;
  readonly title?: React.ReactNode;
  readonly description?: React.ReactNode;
  /**
   * A function receives `requestClose`, so a footer „Abbrechen" goes through the same discard guard
   * as the X button, Escape and the backdrop.
   */
  readonly footer?: React.ReactNode | ((requestClose: () => void) => React.ReactNode);
  /**
   * Extra controls rendered in the header, immediately left of the close (X)
   * button — e.g. a composer's minimise button. Purely additive: modals that
   * don't pass it render exactly as before.
   */
  readonly headerActions?: React.ReactNode;
  /**
   * Suppresses the default header bar (title, description and the X button).
   * For dialogs whose own first row is the chrome — a command palette's search
   * field, for instance — where a second bar above it is noise. A dialog that
   * hides its header has no visible `title` to be named by, so it must pass
   * `ariaLabel`.
   */
  readonly hideHeader?: boolean;
  /**
   * Accessible name for the dialog. Only needed when `hideHeader` removes the
   * `title` that would otherwise name it; a dialog with a header is named by
   * its heading.
   */
  readonly ariaLabel?: string;
  /** `alertdialog` for a confirmation that interrupts the flow. Default `dialog`. */
  readonly role?: 'dialog' | 'alertdialog';
  /**
   * Extra classes for the backdrop, so a caller can change how the card is
   * positioned within it (e.g. `items-center` for a vertically centred
   * overlay). The card itself is styled through `className`.
   */
  readonly backdropClassName?: string;
  /**
   * Extra classes for the body that wraps `children` — chiefly to drop the
   * default `px-6 py-6` for a dialog whose content is edge-to-edge.
   */
  readonly bodyClassName?: string;
  /**
   * Whether closing returns focus to the element that had it when the dialog
   * opened. Default `true`. Set `false` on a close that unmounts that element
   * anyway — restoring would either target a detached node or steal focus from
   * whatever replaced it.
   */
  readonly restoreFocus?: boolean;
  readonly dismissOnBackdrop?: boolean;
  readonly dismissOnEscape?: boolean;
  readonly confirmOnClose?: boolean;
  readonly dirty?: boolean;
  /**
   * Makes every dismiss path inert — the X button (rendered `disabled`), Escape, a backdrop click and
   * `requestClose` — for a dialog that is busy and must not be left mid-operation. The caller owns re-enabling it.
   */
  readonly closeDisabled?: boolean;
  readonly confirmCloseTitle?: string;
  readonly confirmCloseDescription?: string;
  readonly confirmCloseConfirmLabel?: string;
  readonly confirmCloseCancelLabel?: string;
  readonly closeLabel?: string;
  readonly children?: React.ReactNode;
  readonly className?: string;
};

// Stack of currently-open modals — only the topmost responds to Esc, so a nested
// dialog's Escape closes just that dialog, not the modal(s) beneath it.
const openModalStack: symbol[] = [];

export function Modal({
  open,
  onClose,
  size = 'md',
  placement = 'center',
  stacked = false,
  title,
  description,
  footer,
  headerActions,
  hideHeader = false,
  ariaLabel,
  role = 'dialog',
  backdropClassName,
  bodyClassName,
  restoreFocus = true,
  dismissOnBackdrop = false,
  dismissOnEscape = true,
  confirmOnClose = true,
  dirty = true,
  closeDisabled = false,
  confirmCloseTitle,
  confirmCloseDescription,
  confirmCloseConfirmLabel,
  confirmCloseCancelLabel,
  closeLabel,
  children,
  className,
}: ModalProps) {
  const defaults = React.useContext(ModalDefaultsContext);
  const [confirming, setConfirming] = React.useState(false);
  const modalId = React.useRef<symbol>(Symbol('modal'));
  const cardRef = React.useRef<HTMLDivElement | null>(null);
  const titleId = React.useId();
  const bodyRef = React.useRef<HTMLDivElement | null>(null);
  const confirmRef = React.useRef<HTMLDivElement | null>(null);

  // Read at cleanup time, so a close that flips `restoreFocus` in the same
  // render as `open` still sees the intended value.
  const restoreFocusRef = React.useRef(restoreFocus);

  restoreFocusRef.current = restoreFocus;

  // Register in the open-modal stack while open, so the Esc handler can tell whether
  // this modal is the topmost one.
  React.useEffect(() => {
    if (!open) return;
    const id = modalId.current;

    openModalStack.push(id);

    return () => {
      const index = openModalStack.lastIndexOf(id);

      if (index !== -1) openModalStack.splice(index, 1);
    };
  }, [open]);

  const requestClose = React.useCallback(() => {
    if (closeDisabled) return;

    if (confirmOnClose && dirty) {
      setConfirming(true);
    } else {
      onClose();
    }
  }, [closeDisabled, confirmOnClose, dirty, onClose]);

  // Drop the pending discard prompt whenever the dialog itself closes, so a
  // future open never flashes a stale confirmation.
  React.useEffect(() => {
    if (!open) setConfirming(false);
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Only the topmost open modal responds — a nested dialog's Esc must not also
      // dismiss the modal(s) beneath it.
      if (openModalStack[openModalStack.length - 1] !== modalId.current) return;
      // The topmost modal consumes the key: a page-level Esc listener on `window`
      // (a side panel's „close“) would otherwise fire once this close has
      // re-rendered, and one press would dismiss the dialog and the page under it.
      e.stopPropagation();
      // While the discard prompt is up, Esc backs out of it (keep editing)
      // rather than escalating to a forced close.
      if (confirming) {
        setConfirming(false);
      } else if (dismissOnEscape) {
        requestClose();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, dismissOnEscape, confirming, requestClose]);

  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // What had focus when the dialog opened, read while rendering the opening
  // pass: a child's `autoFocus` moves focus at commit, before any effect of
  // this component runs, so an effect would record the child itself and hand
  // focus back to a node that is gone.
  const openerRef = React.useRef<HTMLElement | null>(null);
  const wasOpenRef = React.useRef(false);

  if (open && !wasOpenRef.current) {
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }

  wasOpenRef.current = open;

  // Hand focus back to the opener on close.
  React.useEffect(() => {
    if (!open) return;

    const previous = openerRef.current;

    return () => {
      // A node that left the document with the page around it cannot take focus
      // back, and forcing it would fight whatever now owns the page.
      if (!restoreFocusRef.current || previous === null || !previous.isConnected) return;

      previous.focus();
    };
  }, [open]);

  // Move focus into the dialog once it is on screen, but never onto the header:
  // its first control is the close (X) button, and landing there turns the very
  // next Enter or Space into a dismissal — on a `confirmOnClose` form, into a
  // spurious "discard changes?" prompt on a dialog the user has not touched.
  // Only the body is offered a focus target; the card itself (`tabIndex={-1}`)
  // is the fallback, so focus never stays on the page behind either.
  React.useEffect(() => {
    if (!open) return;

    const container = confirming ? confirmRef.current : cardRef.current;

    if (container === null) return;
    if (container.contains(document.activeElement)) return;

    const preferred = confirming ? container : (bodyRef.current ?? container);

    (getFocusableElements(preferred)[0] ?? container).focus();
  }, [open, confirming]);

  // Keep Tab inside the dialog. Only the topmost modal traps, and only while
  // focus is actually inside it — a menu or popover that portals itself out of
  // the card owns its own focus, and fighting it would break those surfaces.
  React.useEffect(() => {
    if (!open) return;

    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      if (openModalStack[openModalStack.length - 1] !== modalId.current) return;

      const container = confirming ? confirmRef.current : cardRef.current;

      if (container === null || !container.contains(document.activeElement)) return;

      const focusables = getFocusableElements(container);
      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (first === undefined || last === undefined) {
        e.preventDefault();
        container.focus();

        return;
      }

      if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();

        return;
      }

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    };

    document.addEventListener('keydown', handler);

    return () => document.removeEventListener('keydown', handler);
  }, [open, confirming]);

  if (!open) return null;

  const confirmCopy: ConfirmCopy = {
    title: confirmCloseTitle ?? defaults.confirmClose.title,
    description: confirmCloseDescription ?? defaults.confirmClose.description,
    confirmLabel: confirmCloseConfirmLabel ?? defaults.confirmClose.confirmLabel,
    cancelLabel: confirmCloseCancelLabel ?? defaults.confirmClose.cancelLabel,
  };

  const isRight = placement === 'right';

  return createPortal(
    <ModalRequestCloseContext.Provider value={requestClose}>
      <div
        role={role}
        aria-modal="true"
        aria-label={ariaLabel}
        // Named by its heading unless the caller names it outright; without this
        // the role="dialog" had no accessible name at all.
        aria-labelledby={ariaLabel === undefined && !hideHeader && title !== undefined ? titleId : undefined}
        className={cn(
          'fixed inset-0 z-50 flex bg-overlay',
          isRight
            ? 'justify-end'
            : // Below `sm` the sheet sits ON the bottom edge and scrolls inside
              // itself; the backdrop keeps no padding to push it off and no
              // scroll of its own to move it out from under the thumb.
              'items-end justify-center sm:items-start sm:overflow-y-auto sm:p-6 sm:py-12',
          stacked && !isRight && SM_STACKED_TOP,
          backdropClassName,
        )}
        onMouseDown={(e) => {
          if (dismissOnBackdrop && e.target === e.currentTarget) {
            // Suppress the browser's default focus move for this press. React flushes
            // the close synchronously at the end of this discrete event and the
            // `open` effect's cleanup restores focus to the opener — then the default
            // action runs and moves focus to the nearest focusable ancestor of the
            // backdrop, which is nothing, so it lands on <body> and undoes the
            // restore. Escape and the ⌘K chord have no such default action, which is
            // why only this path was broken. It also keeps focus inside a dirty
            // `confirmOnClose` dialog, where this press only raises the discard
            // prompt and the dialog stays up. IMM-553 / BL-031.
            e.preventDefault();
            requestClose();
          }
        }}
      >
        <div
          ref={cardRef}
          tabIndex={-1}
          className={cn(
            'flex w-full flex-col bg-surface outline-none',
            isRight
              ? cn('h-full border-l border-border shadow-xl', panelSizeClass[size])
              : cn(sizeClass[size], stacked && (size === 'full' ? SM_STACKED_FULL_HEIGHT : SM_STACKED_MAX_HEIGHT)),
            className,
          )}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {!hideHeader && (
            <div
              className={cn(
                // `shrink-0`: the card is height-capped, so the chrome must keep
                // its size and let the body absorb the overflow instead.
                'flex shrink-0 items-center justify-between gap-4 px-6 py-3',
                (title !== undefined || description !== undefined) && 'border-b border-border',
              )}
            >
              <div className="flex min-w-0 flex-col gap-1">
                {title !== undefined && (
                  <h2 id={titleId} className="text-xl font-semibold leading-none tracking-tight text-text-primary">
                    {title}
                  </h2>
                )}
                {description !== undefined && <p className="text-base text-text-tertiary">{description}</p>}
              </div>
              <div className="-mr-2 flex shrink-0 items-center gap-1">
                {headerActions}
                <button
                  type="button"
                  onClick={requestClose}
                  disabled={closeDisabled}
                  aria-label={closeLabel ?? defaults.closeLabel}
                  className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-text-tertiary transition-colors hover:bg-hover-bg hover:text-text-primary focus:outline-none focus:shadow-focus disabled:pointer-events-none disabled:opacity-50"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.75}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-4 w-4"
                    aria-hidden
                  >
                    <line x1="6" y1="6" x2="18" y2="18" />
                    <line x1="6" y1="18" x2="18" y2="6" />
                  </svg>
                </button>
              </div>
            </div>
          )}
          {/*
           * `children` is a ReactNode, so `{cond && <X/>}` arrives as the boolean
           * `false` — defined, but renders nothing. An identity check against
           * `undefined` therefore passes and paints an empty `px-6 py-6` band
           * (IMM-507). `React.Children.toArray` drops booleans, null and undefined,
           * so its length is the renderability test. Render `children` (not the
           * `toArray` result — that re-keys and can remount stateful bodies).
           *
           * `min-h-0` is the other half of the card's height cap: a flex child's
           * automatic minimum size is its content, so without it the body refuses
           * to shrink below that content and `overflow-y-auto` stays inert even
           * inside a bounded panel (BL-003 / BL-054).
           */}
          {React.Children.toArray(children).length > 0 && (
            <div ref={bodyRef} className={cn('min-h-0 flex-1 overflow-y-auto px-6 py-6', bodyClassName)}>
              {children}
            </div>
          )}
          {footer !== undefined && (
            // `flex-col-reverse` rather than a reordered DOM: a footer is
            // written [secondary, …, primary] because that is the reading order
            // of a row, and on a sheet the primary belongs on TOP, nearest the
            // thumb. Reversing in CSS keeps one DOM order, so the tab order and
            // the screen reader still follow the written one.
            <div className="flex shrink-0 flex-col-reverse items-stretch gap-2 rounded-b-[inherit] border-t border-border bg-canvas px-4 py-4 sm:flex-row sm:items-center sm:justify-end sm:gap-3 sm:px-6">
              {typeof footer === 'function' ? footer(requestClose) : footer}
            </div>
          )}
        </div>
      </div>

      {confirming && (
        <div
          className="fixed inset-0 z-[60] flex items-end justify-center bg-overlay sm:items-start sm:overflow-y-auto sm:p-6 sm:py-12"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setConfirming(false);
          }}
        >
          <div
            ref={confirmRef}
            tabIndex={-1}
            role="alertdialog"
            aria-modal="true"
            className="flex w-full max-w-none flex-col rounded-b-none rounded-t-2xl bg-surface shadow-xl outline-none sm:max-w-[440px] sm:rounded-lg sm:shadow-lg"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col gap-1 px-6 py-5">
              <h2 className="text-xl font-semibold leading-none tracking-tight text-text-primary">
                {confirmCopy.title}
              </h2>
              <p className="text-base text-text-tertiary">{confirmCopy.description}</p>
            </div>
            <div className="flex flex-col-reverse items-stretch gap-2 rounded-b-[inherit] border-t border-border bg-canvas px-4 py-4 sm:flex-row sm:items-center sm:justify-end sm:gap-3 sm:px-6">
              <Button variant="ghost" type="button" onClick={() => setConfirming(false)}>
                {confirmCopy.cancelLabel}
              </Button>
              <Button
                variant="danger"
                type="button"
                onClick={() => {
                  setConfirming(false);
                  onClose();
                }}
              >
                {confirmCopy.confirmLabel}
              </Button>
            </div>
          </div>
        </div>
      )}
    </ModalRequestCloseContext.Provider>,
    document.body,
  );
}
